import QuoteProductClassSelector from '@/views/components/Selectors/QuoteProductClassSelector';
import QuoteProductDimentionsUomSelector from '@/views/components/Selectors/QuoteProductDimentionsUomSelector';
import { WeightUomSelector } from '@views/screens/common';
import { Col, Row } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { calculateCube, calculateExtVolume, calculateExtWeight } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormTextArea from '../../../../../../components/Inputs/FormTextArea';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import SecondaryLabel from '../../../../../../components/Labels/SecondaryLabel';
import SaveCancelButtons from '../../../SaveCancelButtons';
import {
  cubeRules,
  dimUomRules,
  heightRules,
  lengthRules,
  productCodeRules,
  productDescriptionRules,
  quantityRules,
  weightRules,
  weightUomRules,
  widthRules,
} from './constants';

const HallProductDetailsCard = ({
  index,
  name,
  itemDetails = {},
  remove,
  handleDataChange,
  isLast = false,
  dataTestId,
}) => {
  const editMode = Boolean(itemDetails.editMode);

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const cube = useMemo(
    () => calculateCube(itemDetails?.length, itemDetails?.width, itemDetails?.height, 1, itemDetails?.dimUom),
    [itemDetails?.length, itemDetails?.width, itemDetails?.height, itemDetails?.dimUom],
  );

  const extWeightValue = useMemo(
    () => calculateExtWeight(itemDetails?.quantity, itemDetails?.weight),
    [itemDetails?.quantity, itemDetails?.weight],
  );

  const extVolume = useMemo(
    () => calculateExtVolume(itemDetails?.quantity, itemDetails?.cube),
    [itemDetails?.quantity, itemDetails?.cube],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
  }, [cube]);

  useEffect(() => {
    handleDataChange({ itemSequenceKey: index + 1 }, index);
  }, [index]);

  return (
    <FilterCard
      labelForTestId={dataTestId}
      shadow={false}
      showBorder
      className={isLast ? '' : 'mb-15'}
      customHeader={
        <div className="w-100">
          <Row justify="space-between" align="middle">
            <Col sm={6}>
              <Row align="middle" gutter={15}>
                <Col className="flex-1">
                  <FormInput
                    name={[name, 'productCode']}
                    label={t('translation:getQuote.productDetails.productCode.label')}
                    margin="m-0"
                    editMode={editMode}
                    singleLineMode
                    colon
                    rules={productCodeRules}
                    maxLength="25"
                    dataTestId="prod-details-card-code"
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <SaveCancelButtons handleRemove={() => remove(name)} editMode={false} hideEditButton />
            </Col>
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row gutter={20}>
          <Col sm={4}>
            <FormLabel className="f-14 fw-700">{t('translation:default.fields.productDesc.label')}</FormLabel>
            <FormTextArea
              editMode={editMode}
              margin={!editMode ? 'mb-12' : 'mb-20'}
              name={[name, 'productDescription']}
              rows={editMode ? 4 : 3}
              maxLength={150}
              rules={productDescriptionRules}
              dataTestId="prod-details-card-description"
            />
          </Col>

          <Col sm={11}>
            <Col>
              <Row gutter={15}>
                <Col sm={7}>
                  <FormInput
                    name={[name, 'quantity']}
                    label={t('translation:getQuote.productDetails.quantity')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    rules={quantityRules(t)}
                    dataTestId="prod-details-card-quantity"
                  />
                </Col>

                <Col sm={9}>
                  <FormInput
                    name={[name, 'weight']}
                    label={t('translation:getQuote.productDetails.weight.label')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    placeholder={t('translation:default.fields.weight')}
                    rules={weightRules(t)}
                    dataTestId="prod-details-card-weight"
                  />
                </Col>

                <Col sm={8}>
                  <WeightUomSelector
                    name={[name, 'weightUom']}
                    label={t('translation:getQuote.productDetails.weightUom.label')}
                    margin="m-0"
                    editMode={editMode}
                    placeholder={t('translation:getQuote.productDetails.weightUom.placeholder')}
                    onChange={(val) => handleDataChange({ weightUomName: val?.label }, index)}
                    rules={weightUomRules(t)}
                  />
                </Col>
              </Row>
              <Row gutter={12}>
                <Col sm={5}>
                  <FormInput
                    name={[name, 'length']}
                    label={t('translation:getQuote.productDetails.length.label')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    placeholder="00"
                    rules={lengthRules(t)}
                    dataTestId="prod-details-card-length"
                  />
                </Col>
                <Col sm={5}>
                  <FormInput
                    name={[name, 'width']}
                    label={t('translation:getQuote.productDetails.width.label')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    placeholder="00"
                    rules={widthRules(t)}
                    dataTestId="prod-details-card-width"
                  />
                </Col>
                <Col sm={4}>
                  <FormInput
                    name={[name, 'height']}
                    label={t('translation:getQuote.productDetails.height.label')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    placeholder="00"
                    rules={heightRules(t)}
                    dataTestId="prod-details-card-height"
                  />
                </Col>
                <Col sm={5}>
                  <QuoteProductDimentionsUomSelector
                    name={[name, 'dimUom']}
                    label={t('translation:getQuote.productDetails.dimUom.label')}
                    margin="m-0"
                    editMode={editMode}
                    placeholder={t('translation:getQuote.productDetails.dimUom.placeholder')}
                    onChange={(val) => handleDataChange({ dimUomName: val?.label }, index)}
                    rules={dimUomRules(t)}
                  />
                </Col>

                <Col sm={5}>
                  <FormInput
                    name={[name, 'cube']}
                    label={t('translation:getQuote.productDetails.cube.label')}
                    margin="m-0"
                    editMode={editMode}
                    inputType="number"
                    placeholder="00"
                    min={0}
                    rules={cubeRules(t)}
                    dataTestId="prod-details-card-cube"
                  />
                </Col>
              </Row>
            </Col>
          </Col>

          <Col sm={2}>
            <Row gutter={8}>
              <Col data-testid="product-details-ext-weight">
                <FormLabel className="f-14 fw-700">
                  {t('translation:getQuote.productDetails.extWeight.label')}
                </FormLabel>
                <br />
                <SecondaryLabel className="f-14 fw-600 text-dark">{extWeightValue}</SecondaryLabel>
              </Col>
            </Row>

            <Row gutter={10} className="mt-20">
              <Col data-testid="product-details-ext-volume">
                <FormLabel className="f-14 fw-700">
                  {t('translation:getQuote.productDetails.extVolume.label')}
                </FormLabel>
                <br />
                <SecondaryLabel className="f-14 fw-600 text-dark">{extVolume}</SecondaryLabel>
              </Col>
            </Row>
          </Col>

          <Col sm={3}>
            <QuoteProductClassSelector
              name={[name, 'hazardousClass']}
              label={t('translation:getQuote.productDetails.hazardousClass.label')}
              placeholder={t('translation:getQuote.productDetails.hazardousClass.placeholder')}
              margin="m-0"
              editMode={editMode}
              onChange={(val) => handleDataChange({ className: val?.label }, index)}
              dataTestId="prod-details-card-hazardous-class"
            />
          </Col>

          <Col sm={4}>
            <Row gutter={15}>
              <Col sm={24}>
                <FormInput
                  name={[name, 'harmonizedCode1']}
                  label={t('translation:getQuote.productDetails.harmonizedCode1')}
                  margin="m-0"
                  editMode={editMode}
                  initialValues="500"
                  dataTestId="prod-details-card-harmonized-code-1"
                />
              </Col>
            </Row>

            <Row gutter={20}>
              <Col sm={24}>
                <FormInput
                  name={[name, 'harmonizedCode2']}
                  label={t('translation:getQuote.productDetails.harmonizedCode2')}
                  margin="m-0"
                  editMode={editMode}
                  dataTestId="prod-details-card-harmonized-code-2"
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'itemSequenceKey']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'createdBy']} />
      <HiddenFormInput name={[name, 'billQtyUomName']} />
      <HiddenFormInput name={[name, 'className']} />
      <HiddenFormInput name={[name, 'dimUomName']} />
      <HiddenFormInput name={[name, 'weightUomName']} />
    </FilterCard>
  );
};
export default HallProductDetailsCard;
